import { get } from 'lodash';

import { Controller } from 'react-hook-form';

import {
  DateValuePicker,
  FormItem,
  PasswordInput as LuterkortPasswordInput,
  TextInput as LuterkortTextInput,
} from '@ingrid/luterkort-react';

import { CountrySelect } from '~src/components/CountrySelect/CountrySelect';
import { CurrencySelect } from '~src/components/CurrencySelect/CurrencySelect';
import { ValidationError } from '~src/components/ValidationError/ValidationError';
import { toLocalCalendarDate } from '~src/utils/internationalized-date';

import { ValidateSchema } from '../../create/validate';
import { DynamicPath, useCarrierContractFormContext } from '../../types';
import { useUISchema } from '../hooks';
import { Schema } from '../queries';
import { SchemaFormLabel } from './schema-form-label';

interface Props {
  schema: Schema;
  schemaId: string;
  path?: string;
}

function DateInput({ schemaId, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId)}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <DateValuePicker
          // Due to runtime types (defined by the schema) we need to do some type casting
          // since typescript doesn't really understand this natively.
          defaultValue={value ? toLocalCalendarDate(value as unknown as string) : undefined}
          onChange={value => onChange(value!.toString())}
          isInvalid={fieldState.invalid}
          {...field}
        />
      )}
    />
  );
}

function CurrencyInput({ schemaId, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId)}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <CurrencySelect
          // Due to runtime types (defined by the schema) we need to do some type casting
          // since typescript doesn't really understand this natively.
          value={value as unknown as string | undefined}
          onChange={v => onChange(v)}
          invalid={fieldState.invalid}
          {...field}
        />
      )}
    />
  );
}

function CountryInput({ schemaId, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId)}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <CountrySelect
          // Due to runtime types (defined by the schema) we need to do some type casting
          // since typescript doesn't really understand this natively.
          value={value as unknown as string | undefined}
          onChange={value => onChange(value)}
          invalid={fieldState.invalid}
          {...field}
        />
      )}
    />
  );
}

function PasswordInput({ schemaId, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId)}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <LuterkortPasswordInput
          value={value as unknown as string | undefined}
          onChange={value => onChange(value)}
          data-invalid={fieldState.invalid}
          {...field}
        />
      )}
    />
  );
}

function TextInput({ schemaId, path = '' }: Props) {
  const { control } = useCarrierContractFormContext();

  return (
    <Controller
      rules={ValidateSchema(path, schemaId)}
      name={path as DynamicPath}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <LuterkortTextInput
          value={value as unknown as string | undefined}
          onChange={value => onChange(value)}
          data-invalid={fieldState.invalid}
          {...field}
        />
      )}
    />
  );
}

function StringInput(props: Props) {
  const { widget } = useUISchema({ path: props.path ?? '', schemaId: props.schemaId });

  switch (widget) {
    case 'password':
      return <PasswordInput {...props} />;
    case 'country':
      return <CountryInput {...props} />;
    case 'date':
      return <DateInput {...props} />;
    case 'currency':
      return <CurrencyInput {...props} />;
    default:
      return <TextInput {...props} />;
  }
}

export function SchemaFormString({ schema, schemaId, path = '' }: Props) {
  const { formState } = useCarrierContractFormContext();

  return (
    <FormItem.Root>
      <SchemaFormLabel schema={schema} path={path} schemaId={schemaId} />

      <FormItem.Control invalid={!!get(formState.errors, path)?.message}>
        <StringInput schema={schema} path={path} schemaId={schemaId} />
      </FormItem.Control>

      <ValidationError errors={formState.errors} name={path} />
    </FormItem.Root>
  );
}
